import _ from 'lodash';
import DepsService from 'services/dependencies';
import grid from 'utils/lsp-grid/grid';
import utils from 'utils/lsp-utils-service';
import privileges from 'privilege-templates/logic/privilege-templates';
import { getUserClasses } from 'app/actions/dictionaries.actions';
import { setLearningLink } from 'auth/actions/session.actions';
import { fetchPrivilegeDisplayTypesDictionary } from 'app/actions/dictionaries/privilege-display-types-dictionary.actions';

const DEFAULT_SORT_COLUMN = 'id';
const NA_VALUE = 'n/a';
const STATUS_ACTIVE = 'ACTIVE';
const STATUS_INACTIVE = 'INACTIVE';

const ACTIVE = 'ACTIVE';
const RETAILER_ROLES = ['RETAILER_PRIMARY', 'RETAILER_SECONDARY'];

const APP_CONTEXT = 'app';
const USER_CONTEXT = 'user';

// model initialization
function initializeModel(deps, model) {
  return _.merge(model, {
    user: {
      grid: grid.applyGrid(deps, userList.bind(null, deps), 'user.grid', {
        [deps.config.sortColumnKey]: DEFAULT_SORT_COLUMN,
        [deps.config.pageSizeKey]: deps.config.defaultPageSize[2],
      }),
      myAccount: {},
      editAccount: {},
      editAccountCopy: {},
      changePrimaryUser: grid.applyGrid(deps, getUsers.bind(null, deps), 'user.changePrimaryUser', {
        [deps.config.pageSizeKey]: deps.config.defaultPageSize[2],
      }),
      retailerSettings: {},
    },
  });
}

function registerRestSrvContext(deps) {
  deps.restSrv = deps.restSrv.factory('user', deps);
  return deps;
}

// dictionaries initialization
function loadDictionaries(deps, model) {
  model.dictionary.userStatuses = deps.config.dictionaries.usrAccStatuses;
  model.dictionary.retailerStatuses = deps.config.dictionaries.usrRetailerStatuses;

  return Promise.all([
    loadTemplates(deps, model),
    loadActivateReasons(deps, model),
    loadSuspendReasons(deps, model),
    loadRetailerStatuses(deps, model),
    DepsService.$ngRedux.dispatch(fetchPrivilegeDisplayTypesDictionary()),
  ]).then((models) => models.reduce((reduced, newModel) => _.merge(reduced, newModel), model));
}

// actions
function userList(deps, model, parameters) {
  let newResult = {};
  const urlParameters = {
    userId: RETAILER_ROLES.indexOf(model.session.role) !== -1 ? model.session.id : null,
  };
  const url = model.session.lotteryRepId ? model.session.rest.accounts.lotteryRep.entries.url : model.session.rest.account.entries.url;

  return deps.restSrv.list(USER_CONTEXT, url, parameters, urlParameters).then((result) => {
    newResult = _.cloneDeep(result);
    newResult.content = result.content.map((user) => {
      const retailerId = _.get(user, 'primaryRetailer.retailerId');
      return _.flow([
        (user) => {
          const foundedTemplate = model.dictionary.templates.find((item) => item.id === user.templateId);
          user.employeeTemplate = foundedTemplate ? foundedTemplate.translations[model.session.locale].name : NA_VALUE;
          return user;
        },
        (user) => _.set(user, 'repeatId', `${retailerId}${user.id}`),
        (user) => _.set(user, 'name', `${user.contact.firstName} ${user.contact.lastName}`),
        (user) => _.set(user, 'primaryRetailerId', retailerId || NA_VALUE),
        (user) => _.set(user, 'isActive', user.status && user.status.status === ACTIVE),
      ])(user);
    });
    return newResult;
  });
}

function loadRoles(deps, model) {
  return deps.restSrv.list(USER_CONTEXT, model.session.rest.account.roles.url).then((result) => {
    model.dictionary.roles = result.map((item) =>
      _.flow(
        _.set('displayName', _.capitalize(_.get(item, 'translations[${model.session.locale}].name'))),
        _.set('name', _.get(item, 'translations[${model.session.locale}].name'))
      )(item)
    );
    return model;
  });
}

function loadTemplates(deps, model) {
  return deps.restSrv.list(USER_CONTEXT, model.session.rest.account.templates.url).then((result) => {
    model.dictionary.templates = result.map((item) =>
      _.flow([
        (item) => _.set(item, 'displayName', _.capitalize(_.get(item, 'translations[${model.session.locale}].name'))),
        (item) => _.set(item, 'name', _.get(item, 'translations[${model.session.locale}].name')),
      ])(item)
    );
    return model;
  });
}

function loadUserByParams(restSrv, url, params, model) {
  return restSrv.list(USER_CONTEXT, url, null, params).then((user) =>
    Promise.resolve({
      model,
      user,
    })
  );
}

function loadRetailerStatuses(deps, model) {
  return deps.restSrv
    .listCached(USER_CONTEXT, model.session.rest.navigator.typecodes.url, {
      category: 'rs.retailer.status',
    })
    .then((result) => {
      model.dictionary.retailerLocationStatuses = result;
      return model;
    });
}

function createUser(deps, model, user) {
  const {
    session: { locale },
  } = deps.$ngRedux.getState();
  const userClass = _.cloneDeep(user.userClass);
  const primaryRetailer = _.cloneDeep(user.primaryRetailer);
  user.username = user.contact.emailAddress;
  user.userClass = user.userClass.id;

  if (!user.primaryRetailer && userClass.type === 'INDEPENDENT') {
    user.primaryRetailer = { retailerDisplayNumber: model.session.retailer.retailerDisplayNumber };
  }

  if (user.primaryRetailer && user.primaryRetailer.owner) {
    const salts = model.user.editAccount.retailer ? model.user.editAccount.retailer.salts : null;
    user.primaryRetailer.hashedPins = utils.getHashes(user.primaryRetailer.owner, salts);

    delete user.primaryRetailer.owner;
  }
  const queryParams = { locale };

  return deps.restSrv
    .create(USER_CONTEXT, model.session.rest.account.create.url, user, queryParams)
    .then(() => model)
    .catch((err) => {
      user.userClass = userClass;
      user.primaryRetailer = primaryRetailer;
      throw err;
    });
}

function loadUserByLogin(restSrv, userLogin, model) {
  return loadUserByParams(restSrv, model.session.rest.account.entryByLogin.url, { login: userLogin }, model);
}

function loadUserById(deps, model, id) {
  return loadUserByParams(deps.restSrv, model.session.rest.account.self.data.url, { id }, model);
}

// change primary user
function getUsers(deps, model, parameters) {
  return deps.restSrv.list(USER_CONTEXT, model.session.rest.account.entries.url, parameters);
}

function promoteToPrimary(deps, model, promotedId, downgradedId) {
  return deps.restSrv.update(USER_CONTEXT, model.session.rest.account.promote.url, null, {
    promotedId,
    downgradedId,
  });
}

// edit account
function loadUser(deps, model, id, retailerId) {
  return deps.restSrv
    .list(USER_CONTEXT, model.session.rest.account.entryById.url, null, { id })
    .then((response) => decorateUserModel(deps, response, model))
    .then((newModel) => loadAssignedUser(deps, newModel, newModel.user.editAccount.ownerUserId))
    .then((newModel) => privileges.loadPrivileges(deps, newModel, newModel.user.editAccount.userClass))
    .then((newModel) => loadUserRetailer(deps, newModel, retailerId))
    .then((newModel) => decorateUserPrivileges(newModel))
    .then((newModel) => loadUserPresetName(deps, newModel))
    .then((newModel) => {
      newModel.user.editAccountCopy = _.cloneDeep(newModel.user.editAccount);
      return newModel;
    })
    .catch((error) => {
      console.error(error);
    });
}

function loadAssignedUser(deps, model, id) {
  if (!id || id === NA_VALUE) {
    return model;
  }
  return deps.restSrv.list(USER_CONTEXT, model.session.rest.account.entryById.url, null, { id }).then((response) => {
    model.user.editAccount.assignedUser = {
      name: `${response.contact.lastName} ${response.contact.firstName}`,
    };
    return model;
  });
}

async function decorateUserModel(deps, userModel, model) {
  const newUserModel = _.cloneDeep(userModel);

  const { status } = newUserModel.status;
  const availableStatuses = deps.config.dictionaries.usrAccStatusesDisplay;
  const userClasses = await deps.$ngRedux.dispatch(getUserClasses());

  const userClassLabel = _.find(
    userClasses,
    (userClass) => userClass.id === newUserModel.userClass || userClass.label === newUserModel.userClass
  );
  
  const userAccessLabel = _.find(availableStatuses, (availableStatus) => availableStatus.id === status);
  newUserModel.userClassLabel = userClassLabel ? userClassLabel.label : NA_VALUE;
  newUserModel.userAccessLabel = userAccessLabel || NA_VALUE;
  newUserModel.hasMultipleStores = newUserModel.retailers.length > 1;
  newUserModel.retailerId = _.get(newUserModel, 'retailers[0].retailerId');
  newUserModel.primaryRetailerId = _.get(newUserModel, 'primaryRetailer.retailerId');
  newUserModel.createdLabel = newUserModel.adminCreated
    ? deps.gettextCatalog.getString('Administrator')
    : deps.gettextCatalog.getString('User');

  model.user.editAccount = newUserModel;
  return model;
}

function decorateUserPrivileges(model) {
  const userPresetPrivileges = model.user.editAccount.privileges
    .filter((userPrivilege) => model.privilegesArray.find((priv) => priv.id === userPrivilege.id))
    .map((p) => p.id);
  const { templateId } = model.user.editAccount;
  model.user.editAccount.currentUserPreset = {
    id: templateId,
    privileges: userPresetPrivileges,
  };
  return model;
}

function loadUserRetailer(deps, model, retailerId) {
  if (!retailerId) {
    retailerId = _.get(model.user.editAccount, 'primaryRetailer.retailerId');
  }
  if (!retailerId || retailerId === NA_VALUE) {
    return model;
  }

  const url = _.get(model, 'session.rest.retailer.subordinateData.url');
  return deps.restSrv.list(USER_CONTEXT, url, null, { retailerId }).then((retailer) => {
    const status = _.find(model.dictionary.retailerStatuses, { id: retailer.statusCode }) || '';
    retailer.statusLabel = status.label[model.session.locale] || '';
    model.user.editAccount.retailer = retailer;
    model.user.editAccount.retailerId = retailer.retailerId;
    return model;
  });
}

function loadUserPresetName(deps, model) {
  const templateId = _.get(model.user.editAccount, 'templateId');
  if (!templateId) {
    return model;
  }

  return privileges.loadPreset(deps, model, templateId, model.user.editAccount.userClass).then((p) => {
    model.user.editAccount.currentUserPreset.name = p.translations;
    return model;
  });
}

function loadActivateReasons(deps, model) {
  const queryParams =  { locale: model.session.locale }; 
  return deps.restSrv
    .listCached(USER_CONTEXT, model.session.rest.dictionary.names.url, queryParams, {
      name: 'activate-reasons',
    })
    .then((activateReasons) => {
      model.dictionary.statusChangeReasons[STATUS_ACTIVE] = Object.keys(activateReasons.values).map((key) => ({
        id: key,
        label: activateReasons.values[key],
      }));
      return model;
    });
}

function loadSuspendReasons(deps, model) {
  const queryParams =  { locale: model.session.locale }; 
  return deps.restSrv
    .listCached(USER_CONTEXT, model.session.rest.dictionary.names.url, queryParams, {
      name: 'suspend-reasons',
    })
    .then((suspendReasons) => {
      model.dictionary.statusChangeReasons[STATUS_INACTIVE] = Object.keys(suspendReasons.values).map((key) => ({
        id: key,
        label: suspendReasons.values[key],
      }));
      return model;
    });
}

function extractPersonalDataFromUser(user) {
  return {
    firstName: user.contact.firstName,
    lastName: user.contact.lastName,
    contactPhone: user.contact.contactPhone,
    mobilePhone: user.contact.mobilePhone,
    email: user.username,
    roleId: user.role ? user.role.id : user.roleId,
    lotteryRepId: user.lotteryRepId,
    securityQuestions: [],
  };
}

function updatePersonalData(deps, model, user) {
  return deps.restSrv
    .update(USER_CONTEXT, model.session.rest.account.update.url, extractPersonalDataFromUser(user), {
      id: user.id,
    })
    .then(() => loadUser(deps, model, user.id));
}

function updateSubordinateUser(deps, model, user) {
  return deps.restSrv
    .update(USER_CONTEXT, model.session.rest.account.entryById.url, extractPersonalDataFromUser(user), {
      id: user.id,
    })
    .then(() => loadUser(deps, model, user.id, user.retailerId));
}

function extractSuspendDataFromUser(user) {
  return {
    emailAddress: user.emailAddress,
    reasonKey: user.reasonKey || 'NOT_APPLICABLE',
    otherReasonText: user.otherReasonText,
  };
}

function activate(deps, model, user) {
  const data = extractSuspendDataFromUser(user);
  return deps.restSrv
    .create(USER_CONTEXT, model.session.rest.account.activate.url, data, {
      id: user.id,
    })
    .then(() => loadUser(deps, model, user.id));
}

function suspend(deps, model, user) {
  const data = extractSuspendDataFromUser(user);
  return deps.restSrv
    .create(USER_CONTEXT, model.session.rest.account.suspend.url, data, {
      id: user.id,
    })
    .then(() => loadUser(deps, model, user.id));
}

function updateUserPrivileges(deps, model, user) {
  const templateId = _.get(user, 'currentUserPreset.id');
  const privileges = user.privilegeIds.filter((privilegeName) => model.privilegesArray.find((priv) => priv.id === privilegeName));

  return deps.restSrv
    .update(
      USER_CONTEXT,
      model.session.rest.account.privileges.url,
      { templateId, privileges },
      {
        userId: user.id,
      }
    )
    .then(() => loadUser(deps, model, user.id));
}

function completeRegistration(deps, model, user) {
  return deps.restSrv
    .update(USER_CONTEXT, model.session.rest.account.completeRegistration.url, user, {
      id: user.id,
    })
    .then(() => {
      deps.AlertService.doAlertSuccess(deps.gettextCatalog.getString('Account registration completed successfully'));
      return loadUser(deps, model, user.id);
    });
}

function canResendActivationEmail(deps, model, user) {
  return deps.restSrv.create(USER_CONTEXT, model.session.rest.account.resendActivationEmail.url, null, {
    id: user.id,
  });
}

function deleteUser(deps, model) {
  return deps.restSrv
    .remove(USER_CONTEXT, model.session.rest.account.delete.url, { id: model.user.editAccount.id }, null)
    .then(() => model);
}

function authenticateUser(deps, model, user) {
  return deps.restSrv
    .update(USER_CONTEXT, model.session.rest.account.completeRegistration.url, null, {
      id: user.id,
    })
    .then(() => loadUser(deps, model, user.id));
}

function loadLotteryLearningLink(restSrv, params) {
  return restSrv.list(APP_CONTEXT, params.model.session.rest.elearningLink.get.url).then((result) => {
    const link = _.get(result, 'link');
    if (_.get(params, 'user') && link) {
      params.user.lotteryLearningLink = link;
    }
    if (_.get(params, 'model.session') && link) {
      params.model.session.lotteryLearningLink = link;
    }
    if (link) {
      DepsService.$ngRedux.dispatch(setLearningLink(link));
    }
    return params;
  });
}

function getRetailerDetails(deps, model, retailerId) {
  return deps.restSrv
    .list(USER_CONTEXT, model.session.rest.user.retailerSettings.selfRegistration.url, null, {
      retailerId,
    })
    .then((retailer) => {
      model.user.editAccount.retailer = retailer;
      return model;
    });
}

function getRetailerDetailsForSecondary(deps, model, retailerId, isSecondary) {
  return deps.restSrv
    .list(USER_CONTEXT, model.session.rest.user.retailerSettings.secondaryValidation.url, null, {
      retailerId,
      isSecondary,
    })
    .then((retailer) => {
      model.user.editAccount.retailer = retailer;
      return model;
    });
}

function loadUserReportTo(deps, model) {
  const userId = model.session.id;

  return deps.restSrv.list(USER_CONTEXT, model.session.rest.user.reportTo.url, null, {
    userId,
  });
}

export default {
  initializeModel,
  loadDictionaries,
  userList,
  loadUserByLogin,
  loadUserById,
  createUser,
  getUsers,
  promoteToPrimary,
  loadUser,
  loadAssignedUser,
  authenticateUser,
  deleteUser,
  updatePersonalData,
  updateSubordinateUser,
  updateUserPrivileges,
  activate,
  suspend,
  completeRegistration,
  canResendActivationEmail,
  loadLotteryLearningLink,
  registerRestSrvContext,
  getRetailerDetails,
  getRetailerDetailsForSecondary,
  loadRoles,
  loadUserReportTo,
};



// WEBPACK FOOTER //
// ./src/modules/user/logic/user.js